import React, { useMemo, useState } from "react";
import { canAddChild } from "../../engine/instanceTreeUtils";
import { buildChildrenFromLayout } from "../../engine/layoutBuilder";

function typeIcon(type, category) {
  if (type === "module") return "📦";
  if (type === "hardware") return "⚙️";
  if (String(category || "").startsWith("board.")) return "🟫";
  if (String(category || "").startsWith("front.")) return "🚪";
  return "⬛";
}

function typeBadgeClass(type) {
  switch (type) {
    case "module":
      return "bg-purple-100 text-purple-700";
    case "hardware":
      return "bg-amber-100 text-amber-700";
    case "element":
      return "bg-blue-100 text-blue-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function groupDefinitions(items) {
  return {
    module: items.filter((x) => x.nodeType === "module"),
    element: items.filter((x) => x.nodeType === "element"),
    hardware: items.filter((x) => x.nodeType === "hardware"),
  };
}

export default function AddChildDialog({
  open,
  parentNodeType,
  definitions = [],
  onClose,
  onPick,
}) {
  const [search, setSearch] = useState("");
  const [activeType, setActiveType] = useState("all");

  const allowed = useMemo(() => {
    return (definitions || []).filter((def) => {
      const childType = def.nodeType || "element";
      return canAddChild(parentNodeType, childType);
    });
  }, [definitions, parentNodeType]);

  const filtered = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    return allowed.filter((def) => {
      if (activeType !== "all" && def.nodeType !== activeType) return false;

      if (!q) return true;

      return (
        String(def.name || "").toLowerCase().includes(q) ||
        String(def.code || "").toLowerCase().includes(q) ||
        String(def.category || "").toLowerCase().includes(q) ||
        String(def.nodeType || "").toLowerCase().includes(q)
      );
    });
  }, [allowed, search, activeType]);

  const counts = useMemo(() => {
    const grouped = groupDefinitions(allowed);
    return {
      all: allowed.length,
      module: grouped.module.length,
      element: grouped.element.length,
      hardware: grouped.hardware.length,
    };
  }, [allowed]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-6">
      <div className="w-full max-w-5xl max-h-[88vh] bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
          <div>
            <div className="text-xl font-semibold">Add child component</div>
            <div className="text-sm text-gray-500">
              Parent type: <strong>{parentNodeType || "element"}</strong>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded border bg-white"
          >
            Close
          </button>
        </div>

        <div className="p-4 border-b space-y-3 bg-gray-50">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, code, category..."
            className="w-full px-3 py-2 border rounded bg-white"
            autoFocus
          />

          <div className="flex gap-2 flex-wrap">
            {[
              ["all", "All"],
              ["module", "Modules"],
              ["element", "Elements"],
              ["hardware", "Hardware"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveType(key)}
                className={`px-3 py-1 rounded border text-sm ${
                  activeType === key
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white"
                }`}
              >
                {label} ({counts[key] || 0})
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 overflow-auto flex-1">
          {!filtered.length ? (
            <div className="border rounded-xl p-6 bg-gray-50 text-gray-500">
              No allowed definitions found for this parent type.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filtered.map((def) => (
                <button
                  key={def.id}
                  type="button"
                  onPick={(def) => {
  const instance = createChildInstanceFromDefinition(def);

  instance.children = buildChildrenFromLayout(def, definitions);

  onPick?.(instance);
}}
                  className="text-left border rounded-xl p-4 hover:bg-blue-50 hover:border-blue-300 transition bg-white"
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">
                      {typeIcon(def.nodeType, def.category)}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">
                        {def.name || "Unnamed"}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {def.code || "—"}
                      </div>

                      <div className="mt-3 flex gap-2 flex-wrap">
                        <span
                          className={`text-xs px-2 py-1 rounded ${typeBadgeClass(
                            def.nodeType
                          )}`}
                        >
                          {def.nodeType || "element"}
                        </span>
                        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
                          {def.category || "—"}
                        </span>
                      </div>

                      {def.linkedItem ? (
                        <div className="mt-3 text-xs border rounded p-2 bg-white">
                          <div className="font-medium text-gray-700">
                            Catalog item
                          </div>
                          <div className="text-gray-500 truncate">
                            {def.linkedItem.name || def.linkedItem.code}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}